/**
 * Aba Histórico: período de faturamento (default últimos 90 dias), cliente,
 * status e NFe; tabela de faturas com ações marcar pago / estornar / detalhes.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircleCheck, CreditCard, ExternalLink, Eye, Undo2 } from 'lucide-react';
import type { Invoice } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge, EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { useTransientToast } from '@/components/ui/transient-toast';
import { asaasErrorMessage, paymentStatusChip, useChargeInvoice } from './asaas';
import type { ClientRef } from '@/features/tickets/ClientPicker';
import { useDebouncedValue } from '@/features/tickets/use-debounced-value';
import {
  errorMessage,
  invoicesOptions,
  type InvoiceStatusFilter,
} from './billing-queries';
import { formatBRL, formatCompetence, formatDateBR, isoDaysAgo, todayISO } from './billing-format';
import { ClientFilter } from './client-filter';
import {
  INVOICE_STATUS_CHIP,
  InvoiceDetailDialog,
  PayInvoiceDialog,
  ReverseInvoiceDialog,
} from './invoice-dialogs';

type DialogState =
  | { kind: 'detail'; invoice: Invoice }
  | { kind: 'pay'; invoice: Invoice }
  | { kind: 'reverse'; invoice: Invoice };

export function HistoryTab() {
  const [start, setStart] = useState(() => isoDaysAgo(90));
  const [end, setEnd] = useState(() => todayISO());
  const [client, setClient] = useState<ClientRef | null>(null);
  const [status, setStatus] = useState<InvoiceStatusFilter>('all');
  const [nfeInput, setNfeInput] = useState('');
  const nfe = useDebouncedValue(nfeInput.trim(), 300);
  const [dialog, setDialog] = useState<DialogState | null>(null);

  // Cobrança (v9 Etapa 2): mutation por fatura + toast de erro pt-BR. A linha
  // ocupada é derivada da própria mutation (isPending + variables).
  const charge = useChargeInvoice();
  const { toast: chargeToast, showToast: showChargeError } = useTransientToast('error', 6000);

  const generateCharge = (inv: Invoice) => {
    if (charge.isPending) return;
    charge.mutate(inv.id, {
      onError: (err) => showChargeError(asaasErrorMessage(err)),
    });
  };

  const invoicesQ = useQuery(
    invoicesOptions({
      billing_start: start,
      billing_end: end,
      client_ids: client ? [client.id] : undefined,
      status,
      nfe: nfe || undefined,
    })
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border bg-card p-4 shadow-xs">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="history-start" className="text-[13px]">
            Faturamento de
          </Label>
          <Input
            id="history-start"
            type="date"
            className="h-8 w-40"
            value={start}
            onChange={(e) => {
              if (e.target.value) setStart(e.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="history-end" className="text-[13px]">
            até
          </Label>
          <Input
            id="history-end"
            type="date"
            className="h-8 w-40"
            value={end}
            onChange={(e) => {
              if (e.target.value) setEnd(e.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-[13px]">Cliente</Label>
          <ClientFilter selected={client} onSelect={setClient} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="history-status" className="text-[13px]">
            Status
          </Label>
          <Select
            id="history-status"
            className="h-8 w-36"
            value={status}
            onChange={(e) => setStatus(e.target.value as InvoiceStatusFilter)}
          >
            <option value="all">Todos</option>
            <option value="billed">Faturados</option>
            <option value="paid">Pagos</option>
            <option value="reversed">Estornados</option>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="history-nfe" className="text-[13px]">
            NFe
          </Label>
          <Input
            id="history-nfe"
            className="h-8 w-32"
            value={nfeInput}
            onChange={(e) => setNfeInput(e.target.value)}
            placeholder="Número…"
          />
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
        {invoicesQ.isPending ? (
          <div aria-busy className="space-y-3 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-1/5" />
                <Skeleton className="h-4 w-1/5" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-16 rounded-sm" />
              </div>
            ))}
          </div>
        ) : invoicesQ.isError ? (
          <EmptyState message={`Erro ao carregar faturas: ${errorMessage(invoicesQ.error)}`} />
        ) : invoicesQ.data.length === 0 ? (
          <EmptyState message="Nenhuma fatura no período selecionado" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-9 px-4 font-semibold">Data</th>
                  <th className="h-9 px-4 font-semibold">Cliente</th>
                  <th className="h-9 px-4 font-semibold">Contrato</th>
                  <th className="h-9 px-4 font-semibold">Competência</th>
                  <th className="h-9 px-4 font-semibold">Vencimento</th>
                  <th className="h-9 px-4 font-semibold">Valor Real</th>
                  <th className="h-9 px-4 font-semibold">NFe</th>
                  <th className="h-9 px-4 font-semibold">Status</th>
                  <th className="h-9 px-4 text-right font-semibold">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoicesQ.data.map((inv) => {
                  const chip = INVOICE_STATUS_CHIP[inv.status];
                  const payChip = inv.paymentStatus
                    ? paymentStatusChip(inv.paymentProvider, inv.paymentStatus)
                    : null;
                  return (
                    <tr
                      key={inv.id}
                      className="group h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                    >
                      <td className="px-4 whitespace-nowrap tabular-nums">
                        {formatDateBR(inv.billingDate)}
                      </td>
                      <td className="max-w-44 truncate px-4" title={inv.clientName}>
                        {inv.clientName}
                      </td>
                      <td className="max-w-52 truncate px-4" title={inv.contractName}>
                        {inv.contractName}
                      </td>
                      <td className="px-4 whitespace-nowrap tabular-nums">
                        {formatCompetence(inv.competence)}
                      </td>
                      <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                        {formatDateBR(inv.dueDate)}
                      </td>
                      <td className="px-4 font-semibold whitespace-nowrap tabular-nums">
                        {formatBRL(inv.realValue)}
                      </td>
                      <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                        {inv.nfeNumber ?? '—'}
                      </td>
                      <td className="px-4 whitespace-nowrap">
                        <span className="flex flex-wrap items-center gap-1">
                          <Badge variant={chip.variant}>{chip.label}</Badge>
                          {payChip ? (
                            <Badge variant={payChip.variant} className="px-1.5 py-0 text-[11px]">
                              {payChip.label}
                            </Badge>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-4">
                        <div className="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                          {inv.status === 'billed' && !inv.externalPaymentId ? (
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              title="Gerar cobrança"
                              aria-label={`Gerar cobrança para a fatura de ${inv.clientName}`}
                              disabled={charge.isPending}
                              onClick={() => generateCharge(inv)}
                            >
                              {charge.isPending && charge.variables === inv.id ? (
                                <Spinner className="size-3" />
                              ) : (
                                <CreditCard />
                              )}
                            </Button>
                          ) : null}
                          {inv.paymentUrl ? (
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              title="Ver cobrança"
                              aria-label={`Ver cobrança da fatura de ${inv.clientName}`}
                              asChild
                            >
                              <a href={inv.paymentUrl} target="_blank" rel="noreferrer">
                                <ExternalLink />
                              </a>
                            </Button>
                          ) : null}
                          {inv.status === 'billed' ? (
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              title="Marcar pago"
                              aria-label={`Marcar fatura de ${inv.clientName} como paga`}
                              className="text-success hover:text-success"
                              onClick={() => setDialog({ kind: 'pay', invoice: inv })}
                            >
                              <CircleCheck />
                            </Button>
                          ) : null}
                          {inv.status === 'billed' || inv.status === 'paid' ? (
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              title="Estornar"
                              aria-label={`Estornar fatura de ${inv.clientName}`}
                              className="text-destructive hover:bg-destructive/5 hover:text-destructive"
                              onClick={() => setDialog({ kind: 'reverse', invoice: inv })}
                            >
                              <Undo2 />
                            </Button>
                          ) : null}
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            title="Detalhes"
                            aria-label={`Detalhes da fatura de ${inv.clientName}`}
                            onClick={() => setDialog({ kind: 'detail', invoice: inv })}
                          >
                            <Eye />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast de erro da geração de cobrança. */}
      {chargeToast}

      {dialog?.kind === 'detail' ? (
        <InvoiceDetailDialog invoice={dialog.invoice} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'pay' ? (
        <PayInvoiceDialog invoice={dialog.invoice} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'reverse' ? (
        <ReverseInvoiceDialog invoice={dialog.invoice} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}
